import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { API_URL } from '../../lib/config';
import { getApiErrorMessage } from '../../lib/apiError';
import { clearLinkFromAddressBar, readLinkToken } from '../../lib/linkToken';
import {
  describeAlert,
  describeProtection,
  isNewSignIn,
  type AlertView,
} from './securityNotMe.helpers';
import { NAVY, NAVY_LIGHT, GOLD, GOLD_LIGHT } from '../../styles/theme';

type Phase = 'loading' | 'confirm' | 'working' | 'protected' | 'invalid';

/**
 * Lands the "this wasn't me" link from an account-change email.
 *
 * Deliberate, not incidental:
 *
 *  1. Opening the page changes NOTHING. Mail scanners and link previewers open
 *     links on their own; the hold is placed only by the button below.
 *  2. No login. The person here is reporting that someone else may hold their
 *     session, and may not be able to sign in at all. The emailed token is the
 *     credential, and it is stripped from the address bar before any request and
 *     sent in a POST body.
 */
export default function SecurityNotMe(): JSX.Element {
  const [phase, setPhase] = useState<Phase>('loading');
  const [alert, setAlert] = useState<AlertView | null>(null);
  const [message, setMessage] = useState('');
  const token = useRef('');
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    token.current = readLinkToken();
    clearLinkFromAddressBar();
    if (!token.current) {
      setPhase('invalid');
      setMessage('This link is incomplete. Please contact support right away.');
      return;
    }
    axios
      .post<AlertView>(`${API_URL}/api/v1/security/not-me/inspect`, { token: token.current })
      .then((res) => {
        setAlert(res.data);
        if (res.data.state === 'open') {
          setPhase('confirm');
        } else if (res.data.state === 'used') {
          setPhase('protected');
          setMessage('This report was already received. Your account is protected.');
        } else {
          setPhase('invalid');
          setMessage('This link has expired. Please contact support right away.');
        }
      })
      .catch((err: unknown) => {
        setPhase('invalid');
        setMessage(getApiErrorMessage(err, 'This link is not valid. Please contact support.'));
      });
  }, []);

  const report = useCallback(async () => {
    setPhase('working');
    try {
      const res = await axios.post<{ message: string }>(
        `${API_URL}/api/v1/security/not-me/report`,
        { token: token.current },
      );
      setMessage(res.data.message);
      setPhase('protected');
    } catch (err: unknown) {
      setMessage(getApiErrorMessage(err, 'We could not record your report. Please contact support.'));
      setPhase('invalid');
    }
  }, []);

  const heading = {
    loading: 'One moment…',
    confirm: alert && isNewSignIn(alert) ? 'Was this you?' : 'Did you make this change?',
    working: 'Protecting your account…',
    protected: 'Your account is protected',
    invalid: 'We could not open this link',
  }[phase];

  return (
    <div
      className="min-h-screen flex items-center justify-center px-4"
      style={{ background: `linear-gradient(135deg, ${NAVY}, ${NAVY_LIGHT})` }}
    >
      <div
        className="w-full max-w-md rounded-2xl p-8"
        style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
      >
        <h1 className="text-xl font-semibold mb-3" style={{ color: '#FFFFFF' }}>
          {heading}
        </h1>

        {phase === 'confirm' && alert && (
          <>
            <p className="text-sm leading-relaxed mb-4" style={{ color: 'rgba(255,255,255,0.7)' }}>
              {describeAlert(alert)}
            </p>
            <p className="text-sm leading-relaxed mb-6" style={{ color: 'rgba(255,255,255,0.7)' }}>
              {describeProtection(alert)}
            </p>
            <button
              type="button"
              onClick={() => void report()}
              className="w-full rounded-xl px-6 py-3 text-sm font-semibold mb-3"
              style={{ background: '#B91C1C', color: '#FFFFFF' }}
            >
              {alert && isNewSignIn(alert) ? (
                <>No, secure my account</>
              ) : (
                <>This wasn&apos;t me — protect my account</>
              )}
            </button>
            <Link to="/login" className="block text-center text-sm" style={{ color: GOLD_LIGHT }}>
              It was me — nothing to do
            </Link>
          </>
        )}

        {phase === 'invalid' && (
          <p
            className="text-sm leading-relaxed mb-6"
            role="alert"
            style={{ color: 'rgba(255,255,255,0.7)' }}
          >
            {message}
          </p>
        )}
        {phase !== 'confirm' && phase !== 'invalid' && (
          <output
            className="block text-sm leading-relaxed mb-6"
            style={{ color: 'rgba(255,255,255,0.7)' }}
          >
            {message}
          </output>
        )}

        {(phase === 'protected' || phase === 'invalid') && (
          <Link
            to="/login"
            className="inline-block rounded-xl px-6 py-3 text-sm font-semibold no-underline"
            style={{ background: `linear-gradient(135deg, ${GOLD}, ${GOLD_LIGHT})`, color: NAVY }}
          >
            Continue to sign in
          </Link>
        )}
      </div>
    </div>
  );
}
